import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Form } from '@/components/ui/form';
import { TextField } from '@/components/text-field';
import { Camera, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { updateWorkspace } from '@/services/workspace';
import { errorHandler } from '@/lib/utils';
import { TelegramChatIdHelp } from '@/components/telegram-chat-id-help';
import { TelegramChatIdTestButton } from '@/components/telegram-chat-id-test-button';
// Explicit ObjectSchema annotation: without it, yupResolver's TFieldValues
// generic infers as the loose `FieldValues` type, breaking `Control<T>`
// propagation through TextField. Same pattern as schema/campaign-schema.ts.
const schema = object({
    name: string().required(),
    telegramChatId: string().optional(),
});
export const DetailCard = ({ workspace }) => {
    const { t } = useTranslation();
    const [logoFile, setLogoFile] = React.useState(null);
    const [logoPreview, setLogoPreview] = React.useState(workspace.logoUrl ?? null);
    const form = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            name: workspace.name,
            telegramChatId: workspace.telegramChatId ?? '',
        },
    });
    const { mutate, isPending } = useMutation({
        mutationFn: updateWorkspace,
        onSuccess: (data) => {
            toast.success(data.message);
        },
        onError: errorHandler,
    });
    const isOwner = workspace.role === 'OWNER';
    const initialName = workspace.name;
    const onSubmit = React.useCallback((data) => {
        const formData = new FormData();
        formData.append('name', data.name);
        // Always send to allow clearing — empty string clears the chat ID server-side.
        formData.append('telegramChatId', (data.telegramChatId ?? '').trim());
        if (logoFile) {
            formData.append('imageFile', logoFile);
        }
        mutate({ workspaceId: workspace.publicId, data: formData });
    }, [logoFile, mutate, workspace.publicId]);
    return (<Card>
            <CardHeader>
                <CardTitle className="text-base md:text-lg">{t('workspaces.details', { ns: 'glossary' })}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Name */}
                <Form {...form}>
                    <div className="space-y-1">
                        <Label className="text-xs uppercase text-muted-foreground">
                            {t('workspaces.name.label', { ns: 'glossary' })}
                        </Label>

                        {isOwner ? (<TextField control={form.control} name="name"/>) : (<p className="text-sm font-medium">{initialName}</p>)}

                        <p className="text-xs text-muted-foreground">{t('workspaces.name.help', { ns: 'glossary' })}</p>
                    </div>
                </Form>
                {/* Logo */}
                <div className="space-y-1">
                    <Label className="text-xs uppercase text-muted-foreground">
                        {t('workspaces.image.label', { ns: 'glossary' })}
                    </Label>

                    <div className="flex items-center gap-4">
                        <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-muted">
                            {logoPreview ? (<img src={logoPreview} alt="Workspace logo" className="h-full w-full object-cover"/>) : (<Camera className="h-6 w-6 text-muted-foreground"/>)}
                        </div>

                        {isOwner && (<label className="cursor-pointer text-sm text-primary hover:underline">
                                {t('change')}
                                <input type="file" accept="image/*" hidden onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file)
                    return;
                setLogoFile(file);
                setLogoPreview(URL.createObjectURL(file));
            }}/>
                            </label>)}
                    </div>

                    <p className="text-xs text-muted-foreground">{t('workspaces.image.help', { ns: 'glossary' })}</p>
                </div>

                {/* Telegram notifications */}
                <Form {...form}>
                    <div className="space-y-1">
                        <Label className="text-xs uppercase text-muted-foreground flex items-center justify-between">
                            <span className="flex items-center gap-1.5">
                                <Send className="h-3 w-3"/>
                                {t('workspaces.telegram_chat_id.label', {
            defaultValue: 'Telegram chat ID (default)',
        })}
                            </span>
                            <TelegramChatIdHelp workspaceId={workspace.publicId}/>
                        </Label>

                        {isOwner ? (<div className="flex items-center gap-2">
                                <div className="flex-1">
                                    <TextField control={form.control} name="telegramChatId" placeholder="-1001234567890" autoComplete="off"/>
                                </div>
                                <TelegramChatIdTestButton chatId={form.watch('telegramChatId')} source={{ kind: 'workspace', workspaceId: workspace.publicId }}/>
                            </div>) : (<p className="text-sm font-medium">{workspace.telegramChatId ?? '—'}</p>)}

                        <p className="text-xs text-muted-foreground">
                            {t('workspaces.telegram_chat_id.help', {
            defaultValue: 'Default chat for feedback-campaign submissions. Add the Adora bot to the chat first. Each feedback campaign can override this.',
        })}
                        </p>
                    </div>
                </Form>

                {/* Save */}
                {isOwner && (<div className="pt-4">
                        <Button onClick={form.handleSubmit(onSubmit)} disabled={isPending}>
                            {isPending ? t('saving') : t('save')}
                        </Button>
                    </div>)}
            </CardContent>
        </Card>);
};
